"use client";

import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import Image from "next/image";
import { useState } from "react";
import { motion } from "framer-motion";
import ShareIcon from "@mui/icons-material/Share";
import ScoreCardVideo from "../ScoreCardVideo";
import profileMan from "../../../public/icons/avatar_m.svg";
import profileWoman from "../../../public/icons/avatar_w.svg";
import { usePathname } from "next/navigation";
import { useWindowSize } from "../../hooks/useWindowSize";
import ScorecardDataTable from "./ScorecardDataTable";
import clsx from "clsx";

interface ScoreCardProps {
  playerId: string;
  playerScoreCardId: string;
  shouldIntervalRefetch: boolean;
  profilePicture: string | null;
  playerName: string;
  playerHcp: string;
  playerGender: string;
  fetchData: boolean;
  isOpen?: boolean;
  setHasScorecardLoaded?: (hasLoaded: boolean) => void;
  playerMuted?: boolean;
  showVideoPlaceholder?: boolean;
  isNineHoles?: boolean;
  isMultiRoundTournament?: boolean;
  multiRoundScoreCardIds: { scorecardId: string; roundNumber: number }[];
  latestRound?: number;
}

const shareMessageVariants = {
  initial: {
    opacity: 0,
  },
  active: {
    opacity: [1, 0],
    transition: {
      duration: 3,
    },
  },
};

const ScoreCard = ({
  playerId,
  playerScoreCardId,
  shouldIntervalRefetch,
  profilePicture,
  playerName,
  playerHcp,
  playerGender,
  fetchData = false,
  isOpen = false,
  playerMuted,
  showVideoPlaceholder,
  setHasScorecardLoaded,
  isNineHoles,
  isMultiRoundTournament,
  multiRoundScoreCardIds,
  latestRound,
}: ScoreCardProps) => {
  const { isMobile } = useWindowSize();
  const [currentHoleNumber, setCurrentHoleNumber] = useState(1);
  const [currentShotNumber, setCurrentShotNumber] = useState(1);
  const [linkCopied, setLinkCopied] = useState(false);
  const [currentRoundScorecard, setCurrentRoundScorecard] = useState({
    roundNumber: latestRound,
    scorecardId: playerScoreCardId,
  });

  const pathname = usePathname();

  const origin =
    typeof window !== "undefined" && window.location.origin
      ? window.location.origin
      : "";

  const { data: scoreCardData, isLoading } = useQuery(
    ["player-scorecard-data", currentRoundScorecard.scorecardId],
    () => {
      return axios.post("/api/scorecard", {
        variables: {
          scorecardId: currentRoundScorecard.scorecardId,
        },
      });
    },
    {
      enabled: fetchData,
      refetchInterval: shouldIntervalRefetch && 1000 * 30, // 30s
      refetchIntervalInBackground: shouldIntervalRefetch,
      refetchOnWindowFocus: false,
      onSuccess: (scData) => {
        // check if initial hole is 1 for front nine or full 18 holes
        // or if playing only back nine and initial hole is 10.
        if (
          currentHoleNumber === 1 &&
          scData?.data?.data?.node?.holes[0].holeNumber !== 1
        ) {
          setCurrentHoleNumber(scData?.data?.data?.node?.holes[0].holeNumber);
        }
        if (setHasScorecardLoaded) {
          setHasScorecardLoaded(true);
        }
      },
    }
  );

  const videoData = scoreCardData?.data?.data?.node?.holes;

  const currentVideo = videoData
    ?.find(
      (hole: { holeNumber: number }) => hole.holeNumber === currentHoleNumber
    )
    ?.shots?.find(
      (shot: { shotNumber: number }) => shot.shotNumber === currentShotNumber
    )?.videos?.[0]?.videoUrl;

  return (
    <>
      <div className="flex flex-row justify-between mt-7 mr-2 mb-0 ml-5 md:mt-12 md:mr-12 md:ml-[100px]">
        <div className="flex gap-4">
          <div className="relative m-auto h-10 w-10 rounded-full overflow-hidden bg-tm-c-gray-950 lg:h-[50px] lg:w-[50px]">
            {profilePicture ? (
              <Image
                src={profilePicture}
                alt={playerName || "Profile picture"}
                fill
                sizes="100vw"
                className="object-cover"
              />
            ) : (
              <Image
                src={
                  playerGender?.toLowerCase() === "female"
                    ? profileWoman
                    : profileMan
                }
                alt={playerName || "Profile picture"}
                fill
                sizes="100vw"
                className="object-cover"
              />
            )}
          </div>

          <div
            className={clsx("max-w-[140px] lg:max-w-full", {
              "min-w-[200px]": isLoading,
            })}
          >
            <h3 className="text-xl font-normal m-0">{playerName}</h3>
            <p className="text-[15px] m-0 flex gap-2.5 items-center">
              <span>HCP</span>
              <span>
                {parseFloat(playerHcp) < 0
                  ? `+${-1 * parseFloat(playerHcp)}`
                  : playerHcp}
              </span>
            </p>
            <p className="text-[15px] m-0 flex gap-2.5 items-center">
              Tee:
              {isLoading ? (
                <span
                  style={{ height: "18px", width: "40px" }}
                  className="inline-block h-[15px] w-[20px] bg-tm-c-gray-950 opacity-60 rounded-[10px]"
                />
              ) : (
                <span>{scoreCardData?.data?.data?.node?.player?.tee}</span>
              )}
            </p>
          </div>
        </div>
        <div className="flex flex-col-reverse gap-2 md:flex-row md:gap-5">
          {isMultiRoundTournament && (
            <>
              <div
                role="button"
                className={clsx(
                  "w-fit rounded-md px-2.5 py-1.5 leading-none text-xs flex items-center gap-1.5 cursor-pointer transition-all duration-300 h-[32px] hover:opacity-60",
                  {
                    "bg-tm-c-gray-800": currentRoundScorecard.roundNumber !== 1,
                    "bg-tm-c-brand": currentRoundScorecard.roundNumber === 1,
                  }
                )}
                onClick={() => {
                  const getRound = multiRoundScoreCardIds.find(
                    (r) => r.roundNumber === 1
                  );
                  if (
                    getRound?.scorecardId &&
                    getRound?.scorecardId !== currentRoundScorecard.scorecardId
                  ) {
                    setCurrentRoundScorecard({
                      scorecardId: getRound.scorecardId,
                      roundNumber: getRound.roundNumber,
                    });
                    setCurrentHoleNumber(1);
                  }
                }}
              >
                Round 1
              </div>
              <div
                role="button"
                className={clsx(
                  "w-fit rounded-md px-2.5 py-1.5 leading-none text-xs flex items-center gap-1.5 cursor-pointer transition-all duration-300 h-[32px] hover:opacity-60",
                  {
                    "bg-tm-c-gray-800": currentRoundScorecard.roundNumber !== 2,
                    "bg-tm-c-brand": currentRoundScorecard.roundNumber === 2,
                    "opacity-50 pointer-events-none":
                      !multiRoundScoreCardIds?.find((r) => r.roundNumber === 2)
                        ?.scorecardId,
                  }
                )}
                onClick={() => {
                  const getRound = multiRoundScoreCardIds.find(
                    (r) => r.roundNumber === 2
                  );
                  if (
                    getRound?.scorecardId &&
                    getRound?.scorecardId !== currentRoundScorecard.scorecardId
                  ) {
                    setCurrentRoundScorecard({
                      scorecardId: getRound.scorecardId,
                      roundNumber: getRound.roundNumber,
                    });
                    setCurrentHoleNumber(1);
                  }
                }}
              >
                Round 2
              </div>
            </>
          )}
          <div className="self-end md:self-start">
            <div
              role="button"
              className="bg-tm-c-gray-800 w-fit rounded-md px-2.5 py-1.5 leading-none text-xs flex items-center gap-1.5 cursor-pointer transition-colors duration-300 h-[32px] hover:bg-[rgba(52,52,52,0.6)]"
              onClick={() => {
                const basePath = pathname?.split("?");
                navigator.clipboard.writeText(
                  `${origin}${basePath[0]}?playerid=${playerId}`
                );
                setLinkCopied(true);
              }}
              onMouseLeave={() => setLinkCopied(false)}
            >
              <ShareIcon style={{ fontSize: isMobile ? "large" : "medium" }} />
              {!isMobile && <span>Share</span>}
            </div>
            <motion.span
              variants={shareMessageVariants}
              animate={linkCopied ? "active" : "initial"}
              initial="initial"
              exit="initial"
              className="absolute bg-[rgba(52,52,52,0.6)] text-xs px-2.5 py-[5px] rounded-md mt-[5px] block w-fit right-0 md:right-auto"
              onAnimationComplete={() => {
                if (linkCopied) {
                  setLinkCopied(false);
                }
              }}
            >
              Link copied
            </motion.span>
          </div>
        </div>
      </div>

      <div>
        <div className="flex text-xs mt-[30px] mx-2.5 mb-0 justify-end gap-2.5 flex-wrap md:mx-12 md:my-0 md:gap-5 md:flex-nowrap">
          <div className="flex gap-2.5 items-center w-[calc(50%-5px)] h-fit md:w-auto">
            <span
              className={`flex items-center justify-center leading-normal m-0 p-[2px]
                w-[20px] h-[20px] md:w-[22px] md:h-[22px]
                rounded-full border border-tm-c-light overflow-hidden
              `}
            >
              <span className="bg-tm-c-brand rounded-full w-full h-full" />
            </span>
            <span>Eagle or better</span>
          </div>
          <div className="flex gap-2.5 items-center w-[calc(50%-5px)] h-fit md:w-auto">
            <span
              className={`flex items-center justify-center leading-normal m-0 p-0
                w-[18px] h-[18px] md:w-[20px] md:h-[20px]
                rounded-full bg-tm-c-brand
              `}
            />
            <span>Birdie</span>
          </div>
          <div className="flex gap-2.5 items-center w-[calc(50%-5px)] h-fit md:w-auto">
            <span
              className={`flex items-center justify-center leading-normal bg-tm-c-dark
                m-0 p-0
                w-[18px] h-[18px] md:w-[20px] md:h-[20px]
              `}
            />
            <span>Bogey</span>
          </div>
          <div className="flex gap-2.5 items-center w-[calc(50%-5px)] h-fit md:w-auto">
            <span
              className={`flex items-center justify-center leading-normal m-0 p-0.5
                w-[20px] h-[20px] md:w-[22px] md:h-[22px]
                border border-tm-c-dark overflow-hidden
              `}
            >
              <span className="bg-tm-c-dark w-full h-full" />
            </span>
            <span>Double Bogey+</span>
          </div>
        </div>

        <div className="flex flex-col gap-5 my-2.5 overflow-hidden min-[1311px]:flex-row min-[1311px]:mx-12 min-[1311px]:mb-10 min-[1311px]:gap-0 [&>*]:bg-tm-c-gray-800 [&>*]:w-full">
          <ScorecardDataTable
            scoreCardData={scoreCardData}
            isLoading={isLoading}
            currentHoleNumber={currentHoleNumber}
            currentShotNumber={currentShotNumber}
            setCurrentShotNumber={setCurrentShotNumber}
            setCurrentHoleNumber={setCurrentHoleNumber}
            isFront={scoreCardData?.data?.data?.node?.holes[0].holeNumber === 1}
          />
          {!isNineHoles && (
            <ScorecardDataTable
              isNineHoles={isNineHoles}
              scoreCardData={scoreCardData}
              isLoading={isLoading}
              currentHoleNumber={currentHoleNumber}
              currentShotNumber={currentShotNumber}
              setCurrentShotNumber={setCurrentShotNumber}
              setCurrentHoleNumber={setCurrentHoleNumber}
              isFront={false}
            />
          )}
        </div>
      </div>

      <div className="relative">
        {isLoading && showVideoPlaceholder && (
          <div
            className="flex items-center justify-center w-full h-[180px] bg-[rgba(65,65,65,0.2)] rounded-[10px] 
         md:h-[280px] lg:h-[380px] min-[1311px]:!h-[580px] min-[1311px]:mx-5 min-[1311px]:my-0 min-[1311px]:w-[calc(100%-40px)]"
          ></div>
        )}
        {!currentVideo && !isLoading && showVideoPlaceholder && (
          <div
            className="flex items-center justify-center w-full h-[180px] bg-[rgba(65,65,65,0.2)] rounded-[10px] 
         md:h-[280px] lg:h-[380px] min-[1311px]:!h-[580px] min-[1311px]:mx-5 min-[1311px]:my-0 min-[1311px]:w-[calc(100%-40px)]"
          >
            <div className="w-[60%] text-center lg:w-[40%]">
              <h2 className="text-tm-c-gray-700/60 text-[25px] leading-[1.2]">
                Selected hole has no video available
              </h2>
            </div>
          </div>
        )}
        {currentVideo && (
          <ScoreCardVideo
            playerMuted={playerMuted}
            isScoreCardOpen={isOpen}
            videoData={videoData}
            currentHoleNumber={currentHoleNumber}
            currentShotNumber={currentShotNumber}
            setCurrentShotNumber={setCurrentShotNumber}
            setCurrentHoleNumber={setCurrentHoleNumber}
          />
        )}
      </div>
    </>
  );
};

export default ScoreCard;
